import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import PublicNavbar from '../components/PublicNavbar';
import PublicFooter from '../components/PublicFooter';
import {
  Search,
  CheckCircle,
  Clock,
  FileCheck,
  AlertCircle,
  Loader2,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_META = {
  received: { icon: CheckCircle, color: 'text-blue-600', bg: 'bg-blue-50', ring: 'ring-blue-200' },
  under_review: { icon: Clock, color: 'text-amber-600', bg: 'bg-amber-50', ring: 'ring-amber-200' },
  decision_made: { icon: FileCheck, color: 'text-emerald-600', bg: 'bg-emerald-50', ring: 'ring-emerald-200' },
  unavailable: { icon: AlertCircle, color: 'text-gray-600', bg: 'bg-gray-50', ring: 'ring-gray-200' },
};

const TrackApplicationPage = () => {
  const [searchParams] = useSearchParams();
  const [token, setToken] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);

  const lookup = async (rawToken) => {
    const t = (rawToken || '').trim();
    if (!t) {
      setError('Please enter your tracking token.');
      return;
    }
    setLoading(true);
    setError('');
    setResult(null);
    try {
      const url = `${API_URL}/api/public/driver-portal/application-status?status_token=${encodeURIComponent(t)}`;
      const res = await fetch(url, { method: 'GET' });
      if (!res.ok) {
        if (res.status === 401) throw new Error('This tracking link is invalid or has expired.');
        if (res.status === 403) throw new Error('Application tracking is currently unavailable.');
        if (res.status === 404) throw new Error('We could not find an application for this link.');
        throw new Error('Something went wrong. Please try again later.');
      }
      const data = await res.json();
      setResult(data);
    } catch (e) {
      setError(e.message || 'Unexpected error.');
    } finally {
      setLoading(false);
    }
  };

  // Auto-lookup when a token is present in the URL (?token=...).
  useEffect(() => {
    const urlToken = searchParams.get('token');
    if (urlToken) {
      setToken(urlToken);
      lookup(urlToken);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const formatDate = (iso) => {
    if (!iso) return '—';
    try {
      return new Date(iso).toLocaleString();
    } catch {
      return iso;
    }
  };

  const meta = result ? (STATUS_META[result.status_key] || STATUS_META.unavailable) : null;
  const StatusIcon = meta ? meta.icon : null;

  return (
    <div className="min-h-screen flex flex-col bg-white" data-testid="track-application-page">
      <PublicNavbar />
      <main className="flex-1 max-w-2xl mx-auto w-full px-4 py-14 sm:py-20">
        <div className="mb-8">
          <h1 className="text-4xl sm:text-5xl font-bold text-gray-900 tracking-tight" data-testid="track-title">
            Track Your Application
          </h1>
          <p className="mt-3 text-base text-gray-600">
            Enter the tracking token you received when you submitted your application.
          </p>
        </div>

        <form
          onSubmit={(e) => { e.preventDefault(); lookup(token); }}
          className="flex flex-col sm:flex-row gap-3"
        >
          <input
            type="text"
            value={token}
            onChange={(e) => setToken(e.target.value)}
            placeholder="Paste your tracking token"
            className="flex-1 rounded-lg border border-gray-300 px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            data-testid="track-token-input"
          />
          <button
            type="submit"
            disabled={loading}
            className="inline-flex items-center justify-center gap-2 rounded-lg bg-blue-600 px-6 py-3 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-60 transition-colors"
            data-testid="track-submit-button"
          >
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
            {loading ? 'Checking…' : 'Check Status'}
          </button>
        </form>

        {error && (
          <div
            className="mt-6 flex items-start gap-3 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700"
            data-testid="track-error"
          >
            <AlertCircle className="h-5 w-5 flex-shrink-0" />
            <span>{error}</span>
          </div>
        )}

        {result && meta && (
          <div
            className={`mt-8 rounded-2xl border ring-1 ${meta.ring} ${meta.bg} p-6 sm:p-8`}
            data-testid="track-result"
          >
            <div className="flex items-center gap-4">
              <div className={`rounded-full bg-white p-3 shadow-sm ${meta.color}`}>
                <StatusIcon className="h-7 w-7" />
              </div>
              <div>
                <p className="text-xs uppercase tracking-wider text-gray-500">Current Status</p>
                <p className={`text-2xl font-bold ${meta.color}`} data-testid="track-status-label">
                  {result.status_label}
                </p>
              </div>
            </div>

            <p className="mt-6 text-base text-gray-800" data-testid="track-next-step">
              {result.next_step}
            </p>

            <dl className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-4 border-t border-gray-200 pt-6 text-sm">
              <div>
                <dt className="text-gray-500">Application ID</dt>
                <dd className="font-medium text-gray-900 break-all" data-testid="track-application-id">
                  {result.application_id}
                </dd>
              </div>
              <div>
                <dt className="text-gray-500">Submitted</dt>
                <dd className="font-medium text-gray-900" data-testid="track-submitted-at">
                  {formatDate(result.submitted_at)}
                </dd>
              </div>
              <div>
                <dt className="text-gray-500">Last Updated</dt>
                <dd className="font-medium text-gray-900" data-testid="track-last-updated-at">
                  {formatDate(result.last_updated_at)}
                </dd>
              </div>
            </dl>
          </div>
        )}

        <p className="mt-10 text-sm text-gray-500">
          Don’t have a token?{' '}
          <Link to="/apply" className="text-blue-600 font-medium hover:underline" data-testid="track-apply-link">
            Submit an application
          </Link>
          .
        </p>
      </main>
      <PublicFooter />
    </div>
  );
};

export default TrackApplicationPage;
